import React from 'react';
import { LuCar } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Card from '../../components/ui/Card/Card';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import ErrorState from '../../components/ui/ErrorState/ErrorState';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import VehicleCard from '../../components/domain/VehicleCard/VehicleCard';
import { useUserVehicles } from '../../hooks/useUserVehicles';
import './VehiclesPage.less';

/**
 * My Vehicles — full list of the user's registered vehicles.
 *
 * The registry is admin-managed: users cannot self-add, edit,
 * remove, or set-primary here because the backend doesn't expose
 * mutations for those on the user surface. The page is read-only,
 * with a small explanatory note in the true-empty state so the
 * user knows how to get a vehicle added.
 *
 * Four states are distinguished:
 *   loading   → 2 skeleton cards (do NOT flash "No vehicles yet")
 *   error     → ErrorState with Retry
 *   empty     → EmptyState explaining that GreenCharge manages
 *               registrations; no fake "Add vehicle" CTA
 *   populated → card grid
 */
function VehiclesPage() {
  const { vehicles, isLoading, error, refresh } = useUserVehicles();

  const showLoading  = isLoading && vehicles.length === 0;
  const showError    = !!error;
  const showEmpty    = !isLoading && !error && vehicles.length === 0;
  const showList     = vehicles.length > 0;

  return (
    <Page>
      <PageHeader
        title="My vehicles"
        subtitle="Vehicles registered to your GreenCharge account."
      />

      {showLoading && (
        <div className="gc-vehicles-grid" aria-busy="true" aria-live="polite">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {showError && (
        <ErrorState
          title="Could not load your vehicles"
          description={error}
          onRetry={refresh}
        />
      )}

      {showEmpty && (
        <Card>
          <EmptyState
            icon={<LuCar />}
            title="No vehicles yet"
            description="Vehicles on your GreenCharge account will appear here. If you have already registered one and don't see it, contact support to check the details on file."
          />
        </Card>
      )}

      {showList && (
        <div className="gc-vehicles-grid">
          {vehicles.map((v) => (
            <VehicleCard
              key={v.id || v.plateNormalized}
              vehicle={v}
              to={`/vehicles/${encodeURIComponent(v.plateNormalized)}`}
            />
          ))}
        </div>
      )}
    </Page>
  );
}

export default VehiclesPage;
